import { useEffect, useRef, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Bell, Moon, Sun, LogOut, User, ChevronDown, ChevronsUpDown,
  FolderKanban, Building2, Search, Check, Plus, HelpCircle, Gift, LifeBuoy, Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDarkMode } from "@/hooks/use-dark-mode";
import { useMe } from "@/hooks/use-me";
import { useProjects } from "@/hooks/use-projects";
import { useActiveProject } from "@/hooks/use-active-project";
import { useActiveClient } from "@/hooks/use-active-client";
import { api, media, type ProjectOut } from "@/lib/api";
import { queryClient } from "@/lib/query-client";

function projectAccent(color: string | null): { bg: string; fg: string } {
  switch ((color || "").toLowerCase()) {
    case "good":
    case "#10b981":
      return { bg: "bg-emerald-50 dark:bg-emerald-900/30", fg: "text-emerald-600 dark:text-emerald-300" };
    case "warn":
    case "#f97316":
      return { bg: "bg-orange-50 dark:bg-orange-900/30", fg: "text-warn dark:text-orange-300" };
    case "purple":
    case "#7c3aed":
      return { bg: "bg-violet-50 dark:bg-violet-900/30", fg: "text-violet-600 dark:text-violet-300" };
    case "bad":
    case "#ef4444":
      return { bg: "bg-red-50 dark:bg-red-900/30", fg: "text-bad dark:text-red-300" };
    default:
      return { bg: "bg-brand-50 dark:bg-brand-900/30", fg: "text-brand-600 dark:text-brand-300" };
  }
}

export function Topbar() {
  const { data: me } = useMe();
  const { dark, toggle } = useDarkMode();
  const navigate = useNavigate();
  const { activeId, setActiveId } = useActiveProject();
  const { data: projects } = useProjects({ limit: 200 });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const t = e.target as HTMLElement;
      if (!t.closest("[data-popover]")) {
        setPickerOpen(false);
        setMenuOpen(false);
        setNotifOpen(false);
      }
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Auto-seleccionar primer proyecto si no hay activo válido
  useEffect(() => {
    if (!projects || projects.length === 0) return;
    if (activeId == null || !projects.some((p) => p.id === activeId)) {
      setActiveId(projects[0].id);
    }
  }, [projects, activeId, setActiveId]);

  const active = projects?.find((p) => p.id === activeId) ?? projects?.[0] ?? null;
  const filtered = (projects ?? []).filter((p) =>
    pickerQ.trim() === "" ? true : p.name.toLowerCase().includes(pickerQ.toLowerCase()),
  );

  function handleSelectProject(p: ProjectOut) {
    setActiveId(p.id);
    setPickerOpen(false);
    navigate({ to: "/monitor/$projectId", params: { projectId: String(p.id) } });
  }

  async function handleLogout() {
    await api.logout().catch(() => {});
    queryClient.clear();
    navigate({ to: "/login" });
  }

  const isSuper = me?.role === "superadmin";
  const isScoped = me?.role === "cliente" || me?.role === "supervisor";
  // En el mundo Medios (agencia) el picker global es de cliente, no de proyecto.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const inMediaWorld = pathname.startsWith("/media") || pathname.startsWith("/clients");
  const accent = projectAccent(active?.color ?? null);

  return (
    <header className="h-14 bg-white dark:bg-ink-900 border-b border-ink-200 dark:border-ink-800 px-4 flex items-center gap-3 sticky top-0 z-30">
      {/* Cliente activo — solo staff de agencia en rutas de medios (scoped ya está fijado a un cliente) */}
      {!isSuper && inMediaWorld && !isScoped && <ClientPicker />}

      {/* Project picker — solo si NO superadmin (superadmin no tiene cuenta/proyectos) */}
      {!isSuper && !inMediaWorld && (
        <div className="relative" data-popover ref={popoverRef}>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="h-9 pl-2.5 pr-3 rounded-lg ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-50 dark:hover:bg-ink-800 flex items-center gap-2.5 max-w-xs transition-colors"
            aria-label="Cambiar proyecto activo"
            aria-expanded={pickerOpen}
          >
            <div className={cn("w-6 h-6 rounded-md flex items-center justify-center", accent.bg, accent.fg)}>
              <FolderKanban size={12} />
            </div>
            <div className="text-left min-w-0">
              <div className="text-[10px] text-ink-500 leading-none">Proyecto activo</div>
              <div className="text-xs font-medium text-ink-900 dark:text-white truncate leading-tight mt-0.5">
                {active?.name ?? (projects ? "Sin proyectos" : "Cargando…")}
              </div>
            </div>
            <ChevronsUpDown size={13} className="text-ink-400 shrink-0" />
          </button>

          {pickerOpen && (
            <div className="absolute top-full mt-1.5 left-0 w-80 bg-white dark:bg-ink-900 ring-soft shadow-pop rounded-xl overflow-hidden z-40">
              <div className="px-3 pt-3">
                <div className="flex items-center gap-2 px-2.5 h-8 rounded-md bg-ink-50 dark:bg-ink-800">
                  <Search size={13} className="text-ink-400" />
                  <input
                    autoFocus
                    placeholder="Buscar proyecto…"
                    value={pickerQ}
                    onChange={(e) => setPickerQ(e.target.value)}
                    className="flex-1 bg-transparent text-xs outline-none text-ink-900 dark:text-white"
                  />
                </div>
              </div>
              <ul className="py-1 max-h-72 overflow-y-auto">
                {filtered.length === 0 ? (
                  <li className="px-3 py-6 text-center text-xs text-ink-500">Sin resultados.</li>
                ) : (
                  filtered.map((p) => {
                    const a = projectAccent(p.color);
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectProject(p)}
                          className="w-full px-3 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left"
                        >
                          <div className={cn("w-7 h-7 rounded-md flex items-center justify-center shrink-0", a.bg, a.fg)}>
                            <FolderKanban size={13} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-ink-900 dark:text-white truncate">{p.name}</div>
                            <div className="text-[10px] text-ink-500">
                              {p.networks.length} {p.networks.length === 1 ? "red" : "redes"} ·{" "}
                              {p.mentions_count.toLocaleString("es-PE")} menciones
                            </div>
                          </div>
                          {p.id === active?.id && <Check size={14} className="text-brand-600 shrink-0" />}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
              <div className="border-t border-ink-100 dark:border-ink-800 p-2">
                <button
                  type="button"
                  onClick={() => {
                    setPickerOpen(false);
                    navigate({ to: "/monitor" });
                  }}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-ink-50 dark:hover:bg-ink-800 text-xs text-brand-600 flex items-center gap-2"
                >
                  <Plus size={12} /> Crear nuevo proyecto
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Breadcrumb / status */}
      <div className="hidden md:flex items-center gap-2 text-xs text-ink-500 ml-2">
        <span className="w-1.5 h-1.5 rounded-full bg-good animate-pulse" />
        {isSuper ? (
          <span>Panel superadmin · {me?.email}</span>
        ) : (
          <span>Recolección activa · sincronizado hace 1 min</span>
        )}
      </div>

      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        <button
          onClick={toggle}
          aria-label={dark ? "Modo claro" : "Modo oscuro"}
          title={dark ? "Modo claro" : "Modo oscuro"}
          className="w-9 h-9 rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-600 dark:text-ink-300 flex items-center justify-center"
        >
          {dark ? <Sun size={15} /> : <Moon size={15} />}
        </button>
        <button
          aria-label="Ayuda"
          title="Ayuda"
          className="w-9 h-9 rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-600 dark:text-ink-300 flex items-center justify-center"
        >
          <HelpCircle size={15} />
        </button>

        {/* Notifications */}
        <div className="relative" data-popover>
          <button
            onClick={() => setNotifOpen((v) => !v)}
            aria-label="Notificaciones"
            aria-expanded={notifOpen}
            className="w-9 h-9 rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-600 dark:text-ink-300 flex items-center justify-center relative"
          >
            <Bell size={15} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-warn ring-2 ring-white dark:ring-ink-900" />
          </button>
          {notifOpen && <NotificationsPopover />}
        </div>

        {/* User */}
        <div className="relative ml-1" data-popover>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="h-9 pl-1 pr-2.5 rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 flex items-center gap-2"
            aria-expanded={menuOpen}
          >
            <div className="w-7 h-7 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              {(me?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden md:block text-left">
              <div className="text-xs font-medium text-ink-900 dark:text-white leading-none">{me?.name || "Usuario"}</div>
              <div className="text-[10px] text-ink-500 mt-0.5 leading-none capitalize">{me?.role || ""}</div>
            </div>
            <ChevronDown size={12} className="text-ink-400 ml-0.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-64 bg-white dark:bg-ink-900 ring-soft shadow-pop rounded-xl overflow-hidden z-40">
              <div className="px-4 py-3 border-b border-ink-100 dark:border-ink-800">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-bold flex items-center justify-center">
                    {(me?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink-900 dark:text-white truncate">{me?.name}</div>
                    <div className="text-xs text-ink-500 truncate">{me?.email}</div>
                  </div>
                </div>
              </div>
              <ul className="py-1 text-sm">
                <li>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate({ to: "/settings" });
                    }}
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left text-ink-700 dark:text-ink-200"
                  >
                    <User size={14} /> Mi perfil
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate({ to: "/settings" });
                    }}
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left text-ink-700 dark:text-ink-200"
                  >
                    <Settings size={14} /> Configuración
                  </button>
                </li>
                <li>
                  <button className="w-full px-4 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left text-ink-700 dark:text-ink-200">
                    <Gift size={14} /> Novedades
                  </button>
                </li>
                <li>
                  <button className="w-full px-4 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left text-ink-700 dark:text-ink-200">
                    <LifeBuoy size={14} /> Centro de ayuda
                  </button>
                </li>
                <li className="border-t border-ink-100 dark:border-ink-800 mt-1">
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left text-bad"
                  >
                    <LogOut size={14} /> Cerrar sesión
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function ClientPicker() {
  const { activeClientId, setActiveClientId } = useActiveClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data: clients } = useQuery({
    queryKey: ["media", "clients", "active"],
    queryFn: () => media.listClients({ is_active: true, limit: 200 }),
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      const t = e.target as HTMLElement;
      if (!t.closest("[data-popover]")) setOpen(false);
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Cliente activo desactivado o eliminado → volver a "Todos los clientes".
  useEffect(() => {
    if (activeClientId != null && clients && !clients.some((c) => c.id === activeClientId)) {
      setActiveClientId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, activeClientId]);

  const active = activeClientId != null
    ? (clients ?? []).find((c) => c.id === activeClientId) ?? null
    : null;
  const filtered = (clients ?? []).filter((c) =>
    q.trim() === "" ? true : c.name.toLowerCase().includes(q.toLowerCase()),
  );

  function select(id: number | null) {
    setActiveClientId(id);
    setOpen(false);
    setQ("");
  }

  return (
    <div className="relative" data-popover>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-9 pl-2.5 pr-3 rounded-lg ring-1 ring-ink-200 dark:ring-ink-700 hover:bg-ink-50 dark:hover:bg-ink-800 flex items-center gap-2.5 max-w-xs transition-colors"
        aria-label="Cambiar cliente activo"
        aria-expanded={open}
      >
        <div className="w-6 h-6 rounded-md flex items-center justify-center bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300">
          <Building2 size={12} />
        </div>
        <div className="text-left min-w-0">
          <div className="text-[10px] text-ink-500 leading-none">Cliente activo</div>
          <div className="text-xs font-medium text-ink-900 dark:text-white truncate leading-tight mt-0.5">
            {active?.name ?? (clients ? "Todos los clientes" : "Cargando…")}
          </div>
        </div>
        <ChevronsUpDown size={13} className="text-ink-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 w-80 bg-white dark:bg-ink-900 ring-soft shadow-pop rounded-xl overflow-hidden z-40">
          <div className="px-3 pt-3">
            <div className="flex items-center gap-2 px-2.5 h-8 rounded-md bg-ink-50 dark:bg-ink-800">
              <Search size={13} className="text-ink-400" />
              <input
                autoFocus
                placeholder="Buscar cliente…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="flex-1 bg-transparent text-xs outline-none text-ink-900 dark:text-white"
              />
            </div>
          </div>
          <ul className="py-1 max-h-72 overflow-y-auto">
            <li>
              <button
                type="button"
                onClick={() => select(null)}
                className="w-full px-3 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left"
              >
                <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-ink-100 dark:bg-ink-800 text-ink-500">
                  <Building2 size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-900 dark:text-white">Todos los clientes</div>
                  <div className="text-[10px] text-ink-500">Sin filtro por cliente</div>
                </div>
                {activeClientId == null && <Check size={14} className="text-brand-600 shrink-0" />}
              </button>
            </li>
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-ink-500">Sin resultados.</li>
            ) : (
              filtered.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => select(c.id)}
                    className="w-full px-3 py-2 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800 text-left"
                  >
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300">
                      <Building2 size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-900 dark:text-white truncate">{c.name}</div>
                      <div className="text-[10px] text-ink-500">{c.code}</div>
                    </div>
                    {c.id === activeClientId && <Check size={14} className="text-brand-600 shrink-0" />}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function NotificationsPopover() {
  const notes = [
    {
      tone: "warn" as const,
      title: "Pico de menciones detectado",
      body: '"Crisis Logística Mayo" +220% en últimas 2h',
      time: "hace 4 min",
    },
    {
      tone: "brand" as const,
      title: "Nueva conversación urgente",
      body: "Mauricio Lago · Instagram",
      time: "hace 18 min",
    },
    {
      tone: "good" as const,
      title: "Informe semanal generado",
      body: "Reputación de marca · listo para descargar",
      time: "hace 1 h",
    },
  ];
  const toneCls = {
    warn: "bg-orange-50 text-warn dark:bg-orange-900/30 dark:text-orange-300",
    good: "bg-emerald-50 text-good dark:bg-emerald-900/30 dark:text-emerald-300",
    brand: "bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300",
  };
  return (
    <div className="absolute right-0 top-full mt-1.5 w-96 bg-white dark:bg-ink-900 ring-soft shadow-pop rounded-xl overflow-hidden z-40">
      <div className="px-4 py-3 border-b border-ink-100 dark:border-ink-800 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ink-900 dark:text-white">Notificaciones</h3>
          <p className="text-[11px] text-ink-500">3 nuevas hoy</p>
        </div>
        <button className="text-xs text-brand-600 hover:text-brand-700">Marcar leídas</button>
      </div>
      <ul className="max-h-96 overflow-y-auto divide-y divide-ink-100 dark:divide-ink-800">
        {notes.map((n, i) => (
          <li key={i} className="px-4 py-3 hover:bg-ink-50 dark:hover:bg-ink-800/50 flex gap-3">
            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0", toneCls[n.tone])}>
              <Bell size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink-900 dark:text-white">{n.title}</p>
              <p className="text-xs text-ink-500 mt-0.5">{n.body}</p>
              <p className="text-[10px] text-ink-400 mt-1">{n.time}</p>
            </div>
          </li>
        ))}
      </ul>
      <div className="px-4 py-2.5 border-t border-ink-100 dark:border-ink-800 text-center">
        <button className="text-xs text-brand-600 hover:text-brand-700 font-medium">
          Ver todas las notificaciones →
        </button>
      </div>
    </div>
  );
}
